import {useApolloClient} from '@apollo/client';
import {createStyles} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import type {CoreObject} from 'app-core/framework/CoreObjectConstants';
import {starItems, unstarItems} from 'app-core/platform/starrable/StarDAO';
import {assert} from 'Asserts';
import {RealtimeMessageType} from 'realtime/Constants';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import {
  ChatHistoryItemIcon,
  ChatSidepanelItem,
  RenameChatModal,
  updateChatSessionInCache,
} from '../ChatListing';
import {getChatNameRenderInfo} from '../ChatNameUtil';
import type {SidepanelChat} from '../ChatRouter';
import SidebarRow from './SidebarRow';

const useStyles = createStyles((_, {showDots}: {showDots: boolean}) => ({
  dots: {
    visibility: showDots ? 'visible' : 'hidden',
  },
}));

export interface ChatStarUpdate {
  chatId: string;
  isStarred: boolean;
}

export interface RedesignChatRowProps {
  item: ChatSidepanelItem;
  onStarUpdated: (update: ChatStarUpdate) => Promise<void>;
  onSelect: (chat: SidepanelChat) => void;
  isSelected: boolean;
}

/**
 * A single Starred/Recent row in the redesigned chat sidebar. Built on the shared {@link SidebarRow}
 * so it aligns with the nav items, with a dimmed icon. There is no inline star — starring lives in
 * the hover overflow ("…") menu alongside Rename, matching the mock.
 */
export default function RedesignChatRow({
  item,
  onStarUpdated,
  onSelect,
  isSelected,
}: RedesignChatRowProps): JSX.Element {
  const isStarred = Boolean(item.star?.isStarred);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUpdatingStar, setIsUpdatingStar] = useState(false);
  const {classes} = useStyles({showDots: item.id != null && (isHovering || isMenuOpen)});
  const apolloClient = useApolloClient();
  const chatNameInfo = getChatNameRenderInfo(item);

  const onChatNameUpdated = useCallback(
    (message: {data: {name: string; modifiedAt: string}}) => {
      if (item.id != null) {
        updateChatSessionInCache(apolloClient, item.id, message.data.name, message.data.modifiedAt);
      }
    },
    [apolloClient, item.id]
  );

  useRealtimeChannelSubscription(
    item.id != null ? getRealtimeChannelNameForApiId(item.id) : '',
    onChatNameUpdated,
    [RealtimeMessageType.CHAT_NAME_UPDATED]
  );

  const openRenameModal = useCallback(() => {
    assert(item.id != null, 'User attempted to rename a non-persisted chat session');
    const key = `rename-chat-${item.id}`;
    LegacyModalContainer.open(
      key,
      <RenameChatModal
        chatId={item.id}
        currentName={chatNameInfo.text}
        onClose={() => {
          LegacyModalContainer.close(key);
        }}
      />
    );
  }, [item.id, chatNameInfo.text]);

  const handleClickStar = useCallback(async () => {
    if (item.id == null || isUpdatingStar) {
      return;
    }
    const nextIsStarred = !isStarred;
    const toggleStar = nextIsStarred ? starItems : unstarItems;
    setIsUpdatingStar(true);
    try {
      await toggleStar([{id: item.id as CoreObject['id']}]);
    } catch (error) {
      setIsUpdatingStar(false);
      throw error;
    }
    setIsUpdatingStar(false);
    await onStarUpdated({chatId: item.id, isStarred: nextIsStarred});
  }, [isStarred, isUpdatingStar, item.id, onStarUpdated]);

  const dots =
    item.id != null ? (
      <span
        className={classes.dots}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <OverflowMenu
          buttonProps={{'aria-label': 'Chat options'}}
          isIconHorizontal={false}
          isOpen={isMenuOpen}
          menuProps={{dropLeft: true, width: MenuWidth.FIT_CONTENT}}
          onOpenChange={setIsMenuOpen}
          options={[
            {
              icon: 'edit',
              contents: <>Rename</>,
              onSelect: openRenameModal,
            },
            {
              icon: isStarred ? 'star' : 'star-outline',
              contents: <>{isStarred ? 'Unstar' : 'Star'}</>,
              onSelect: handleClickStar,
            },
          ]}
        />
      </span>
    ) : undefined;

  return (
    <SidebarRow
      icon={<ChatHistoryItemIcon item={item} />}
      label={chatNameInfo.node}
      tooltipLabel={chatNameInfo.text}
      dimIcon={true}
      active={isSelected}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      onClick={() => {
        onSelect(item.id != null ? {id: item.id, type: item.type} : {id: null});
      }}
      trailing={dots}
      dataTestAttrs={{
        component: 'RedesignChatRow',
        element: 'root',
        key: item.name ?? '',
        ...(isSelected ? {index: 'active'} : {}),
      }}
    />
  );
}
